import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { Link } from "react-router-dom";

export default function DashboardPage() {
  const { user } = useContext(AuthContext);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState("");
  const [appSettings, setAppSettings] = useState([]);

  // Mapping for setting display names
  const settingDisplayNames = {
    'openweathermap_api': 'Weather API',
    'mapbox_api': 'Routes API'
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch app settings
        const response = await axiosInstance.post("/api/v1/appsettings/");
        setAppSettings(response.data.appsettings || []);
      } catch (err) {
        console.error("Failed to fetch app settings:", err);
        setError("Failed to load app settings. Please try again later.");
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, []);

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.first_name}!</h1>
        <p className="mt-1 text-sm text-gray-500">Here's an overview of your patrol management system.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quick Access */}
        <div className="p-4 rounded-lg border border-gray-100 bg-white shadow-sm">
          <h2 className="text-sm font-medium text-gray-900 mb-4">Quick Access</h2>
          <div className="space-y-3">
            <Link to="/patrolplan" className="block p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">View Patrol Plan</span>
                <span className="text-xs text-gray-500">→</span>
              </div>
            </Link>
            <Link to="/choose-locations" className="block p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Choose Locations</span>
                <span className="text-xs text-gray-500">→</span>
              </div>
            </Link>
            <Link to="/settings" className="block p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Settings</span>
                <span className="text-xs text-gray-500">→</span>
              </div>
            </Link>
          </div>
        </div>

        {/* Your Assigned Areas */}
        <div className="p-4 rounded-lg border border-gray-100 bg-white shadow-sm">
          <h2 className="text-sm font-medium text-gray-900 mb-4">Your Assigned Areas</h2>
          <div className="p-3 rounded-md bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Total Areas</span>
              <span className="text-2xl font-semibold text-customIndigo">{user?.user_areas?.length || 0}</span>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="p-4 rounded-lg border border-gray-100 bg-white shadow-sm">
          <h2 className="text-sm font-medium text-gray-900 mb-4">System Status</h2>
          <div className="space-y-3">
            {loadingSettings ? (
              <div className="flex items-center justify-center p-3 rounded-md bg-gray-50">
                <div className="w-4 h-4 border-2 border-customIndigo border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-2 text-sm text-gray-500">Loading settings...</span>
              </div>
            ) : error ? (
              <div className="p-3 rounded-md bg-red-50">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            ) : (
              appSettings.map((setting) => (
                <div key={setting.setting_name} className="flex items-center justify-between p-3 rounded-md bg-gray-50">
                  <span className="text-sm text-gray-700">
                    {settingDisplayNames[setting.setting_name] || setting.setting_name}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    setting.setting_value ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
                  }`}>
                    {setting.setting_value ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
